import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash, MagnifyingGlass, Brain } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import AIScore from "@/components/AIScore";

const STAGES = [
  { value: "nuevo", label: "Nuevo", color: "rgba(59,130,246,0.12)", text: "#3B82F6" },
  { value: "contactado", label: "Contactado", color: "rgba(168,85,247,0.12)", text: "#A855F7" },
  { value: "calificado", label: "Calificado", color: "rgba(94,106,210,0.15)", text: "#9EA9FF" },
  { value: "negociacion", label: "Negociacion", color: "rgba(245,158,11,0.12)", text: "#F59E0B" },
  { value: "ganado", label: "Ganado", color: "rgba(16,185,129,0.12)", text: "#10B981" },
  { value: "perdido", label: "Perdido", color: "rgba(239,68,68,0.12)", text: "#EF4444" },
];

const StageBadge = ({ stage }) => {
  const s = STAGES.find((x) => x.value === stage) || STAGES[0];
  return (
    <span className="px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider" style={{ background: s.color, color: s.text }}>
      {s.label}
    </span>
  );
};

export default function CRM() {
  const { t } = useI18n();
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [filterStage, setFilterStage] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", stage: "nuevo", tags: "", notes: "" });

  const load = async () => {
    const { data } = await api.get("/contacts");
    setContacts(data);
  };

  useEffect(() => { load(); }, []);

  const filtered = contacts.filter((c) => {
    if (filterStage !== "all" && c.stage !== filterStage) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return c.name.toLowerCase().includes(q) || c.phone.includes(q) || (c.email || "").toLowerCase().includes(q);
  });

  const save = async () => {
    try {
      await api.post("/contacts", {
        ...form,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Contacto creado");
      setOpen(false);
      setForm({ name: "", phone: "", email: "", stage: "nuevo", tags: "", notes: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear");
    }
  };

  const del = async (id) => {
    if (!confirm("Eliminar contacto?")) return;
    await api.delete(`/contacts/${id}`);
    toast.success("Eliminado");
    load();
  };

  const updateStage = async (id, stage) => {
    await api.patch(`/contacts/${id}`, { stage });
    load();
  };

  return (
    <div className="p-8 max-w-7xl mx-auto fade-in">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#71717A] mb-2">{t("crm_kicker")}</div>
          <h1 className="font-display text-4xl font-medium tracking-tighter">{t("crm_title")}</h1>
          <p className="text-sm text-[#A1A1AA] mt-1">{contacts.length} contactos · {filtered.length} mostrados</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#5E6AD2] hover:bg-[#4B55C3]" data-testid="new-contact-button">
              <Plus size={16} className="mr-1.5" weight="bold" /> {t("crm_new")}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121214] border-[#27272A] text-white">
            <DialogHeader><DialogTitle className="font-display tracking-tight">Nuevo contacto</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0C] border-[#27272A] mt-1" data-testid="new-contact-name" /></div>
              <div><Label>Telefono (con codigo pais, ej +52155...)</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#0A0A0C] border-[#27272A] mt-1" data-testid="new-contact-phone" /></div>
              <div><Label>Email (opcional)</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0A0A0C] border-[#27272A] mt-1" data-testid="new-contact-email" /></div>
              <div>
                <Label>Etapa</Label>
                <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                  <SelectTrigger className="bg-[#0A0A0C] border-[#27272A] mt-1" data-testid="new-contact-stage"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121214] border-[#27272A]">
                    {STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Tags (separados por coma)</Label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="bg-[#0A0A0C] border-[#27272A] mt-1" data-testid="new-contact-tags" /></div>
              <div><Label>Notas</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#0A0A0C] border-[#27272A] mt-1" /></div>
              <Button onClick={save} className="w-full bg-[#5E6AD2] hover:bg-[#4B55C3]" data-testid="save-contact-button">Guardar</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-3 mb-5">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
          <Input
            placeholder={t("crm_search")}
            value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="contacts-search"
            className="pl-9 bg-[#121214] border-[#27272A]"
          />
        </div>
        <Select value={filterStage} onValueChange={setFilterStage}>
          <SelectTrigger className="w-48 bg-[#121214] border-[#27272A]" data-testid="filter-stage">
            <SelectValue placeholder="Todas etapas" />
          </SelectTrigger>
          <SelectContent className="bg-[#121214] border-[#27272A]">
            <SelectItem value="all">Todas las etapas</SelectItem>
            {STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-[#27272A] rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="contacts-table">
          <thead className="bg-[#121214] border-b border-[#27272A]">
            <tr className="text-[10px] uppercase tracking-wider text-[#71717A]">
              <th className="text-left px-5 py-3 font-medium">Nombre</th>
              <th className="text-left px-5 py-3 font-medium">Telefono</th>
              <th className="text-left px-5 py-3 font-medium">Etapa</th>
              <th className="text-left px-5 py-3 font-medium">Score IA</th>
              <th className="text-left px-5 py-3 font-medium">Tags</th>
              <th className="text-left px-5 py-3 font-medium">Creado</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-12 text-[#71717A]">Sin contactos</td></tr>
            )}
            {filtered.map((c) => (
              <tr key={c.id} className="border-b border-[#18181B] hover:bg-[#121214] transition-colors" data-testid={`row-${c.id}`}>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2.5">
                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#5E6AD2] to-[#3B82F6] flex items-center justify-center text-xs font-medium">{c.name[0]?.toUpperCase()}</div>
                    <div>
                      <Link to={`/app/contacts/${c.id}`} className="font-medium hover:text-[#9EA9FF]" data-testid={`contact-link-${c.id}`}>{c.name}</Link>
                      {c.email && <div className="text-xs text-[#71717A]">{c.email}</div>}
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3 font-mono text-xs text-[#A1A1AA]">{c.phone}</td>
                <td className="px-5 py-3">
                  <Select value={c.stage} onValueChange={(v) => updateStage(c.id, v)}>
                    <SelectTrigger className="w-36 h-7 bg-transparent border-none text-xs p-0 hover:bg-[#18181B]" data-testid={`stage-${c.id}`}>
                      <StageBadge stage={c.stage} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121214] border-[#27272A]">
                      {STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-5 py-3">
                  <AIScore score={c.ai_score} />
                </td>
                <td className="px-5 py-3">
                  <div className="flex flex-wrap gap-1">
                    {c.tags.map((t) => <span key={t} className="px-2 py-0.5 text-[10px] rounded bg-[#18181B] text-[#A1A1AA] border border-[#27272A]">{t}</span>)}
                  </div>
                </td>
                <td className="px-5 py-3 text-xs text-[#71717A] font-mono">{new Date(c.created_at).toLocaleDateString("es-MX")}</td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => del(c.id)} className="p-1.5 rounded hover:bg-[#27272A] text-[#71717A] hover:text-[#EF4444]" data-testid={`delete-${c.id}`}>
                    <Trash size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
